/**
 * Phase 2E — in-memory application cache.
 *
 * Scope:
 *   - lives ONLY in the browser tab's JS memory
 *   - never writes to localStorage/sessionStorage/IndexedDB
 *   - cleared on logout (useAuth logout triggers clearAll)
 *   - cleared on tab close (automatic)
 *
 * Entries:
 *   { data, cachedAt }  with per-key TTL (default 60s)
 *
 * Hook `useCached(key, fetcher, { ttl })`:
 *   - first mount → fetch, store, render
 *   - subsequent mounts within TTL → render from cache, zero network
 *   - subsequent mounts after TTL → render stale + revalidate silently
 *
 * Mutation invalidation:
 *   `invalidate(keyPrefix)` clears every entry whose key starts with the prefix.
 *   Writes (POST/PUT/DELETE) call this manually.
 *
 * NEVER cache permission-sensitive data (other users' submissions,
 * admin aggregates with identity, Maven AI answers, etc.). Only use for
 * list/detail views whose freshness tolerance is seconds.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const AppCacheCtx = createContext(null);

export function AppCacheProvider({ children }) {
  // useRef(new Map()) stays stable across renders; never serialized.
  const store = useRef(new Map());

  const get = useCallback((key) => store.current.get(key), []);
  const set = useCallback((key, data) => {
    store.current.set(key, { data, cachedAt: Date.now() });
  }, []);
  const invalidate = useCallback((keyPrefix) => {
    for (const k of Array.from(store.current.keys())) {
      if (k.startsWith(keyPrefix)) store.current.delete(k);
    }
  }, []);
  const clearAll = useCallback(() => {
    store.current.clear();
  }, []);
  const sizeHint = useCallback(() => store.current.size, []);

  return (
    <AppCacheCtx.Provider value={{ get, set, invalidate, clearAll, sizeHint }}>
      {children}
    </AppCacheCtx.Provider>
  );
}

export function useAppCache() {
  const ctx = useContext(AppCacheCtx);
  if (!ctx) throw new Error("useAppCache must be used inside AppCacheProvider");
  return ctx;
}

export function useCached(key, fetcher, { ttl = 60_000 } = {}) {
  const cache = useAppCache();
  const entry = cache.get(key);
  const stale = !entry || Date.now() - entry.cachedAt > ttl;
  const [data, setData] = useState(entry?.data ?? null);
  const [loading, setLoading] = useState(!entry);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    try {
      const fresh = await fetcher();
      cache.set(key, fresh);
      setData(fresh);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!entry) {
      setLoading(true);
      refetch();
    } else if (stale) {
      // show cached data immediately, revalidate in background
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, loading, error, refetch };
}
